import 'formdata-polyfill';
import { h } from 'preact';
import * as is from 'utilities/is';
import { ACTIONS } from 'constants/annotations';
import { APPLICATION_EVENT } from 'constants/events';
import { Loader } from 'rendering/components/loader';
import { Confirmer } from 'rendering/components/confirmer';
import { BaseAnnotation } from 'rendering/annotations/base_annotation';
import { COMPOSITION_TYPES, getCompositionInfo } from 'constants/annotations';

export class FormAnnotation extends BaseAnnotation {
    constructor(props) {
        super(props);

        this.state = {
            ...this.state,
            loaded: false,
            confirming: false,
            confirmArgs: {}
        };

        import(/* webpackChunkName: "form-bootstrap-css" */ './form.scss')
            .then((module) => this.setState({ loaded: true }))
    }

    componentDidMount(...args) {
        this.applyStyles(this.formEl);

        if (is.fn(super.componentDidMount)) {
            super.componentDidMount(...args);
        } 
    }

    componentDidUpdate(...args) {
        this.applyStyles(this.formEl);

        if (is.fn(super.componentDidUpdate)) {
            super.componentDidUpdate(...args);
        } 
    }

    onChange = () => (
        this.eventBus.emit(
            APPLICATION_EVENT.FORM_DATA_CHANGE, {
                [this.internal.name]: this.formData
            }
        )
    )

    applyActions(...args) {
        const applied = super.applyActions(...args);

        return {
            ...applied,
            onChange: (...args) => {
                this.onChange(...args);

                if (is.fn(applied.onChange)) {
                    applied.onChange(...args);
                }
            }
        }
    }

    render() {
        if (!this.state.loaded) {
            return (
                <div style={ this.getElementStyles() }>
                    <Loader />
                </div>
            );
        }

        const attrs = {
                ref: el => this.formEl = el,
                style: this.getElementStyles(),
                className: this.classes,
                ...this.focusable,
                ...this.dataAttrs,
                ...this.applyActions(this.actions)
            },
            confirmAttrs = {
                onSubmit: this.confirmHandler,
                styles: this.confirmStyles,
                ...this.state.confirmArgs
            };

        return (
            <form { ...attrs }>
                { this.contents }
                {
                    this.state.confirming ?
                        <Confirmer { ...confirmAttrs }/> :
                        null
                }
            </form>
        )
    }

    showConfirm(args = {}) {
        this.setState({ confirming: true, confirmArgs: args });
    }

    confirmHandler = e => {
        this.onAction('onConfirm', {})
    }

    applyStyles(el) {
        const { FORM } = COMPOSITION_TYPES;

        if (!el) {
            return;
        }

        const styleToSelector = {
            [FORM.HEADER]: 'h1, h2, h3, h4, h5, h6',
            [FORM.PARAGRAPH]: 'p',
            [FORM.LABEL]: 'label',
            [FORM.BUTTON]: 'button',
            [FORM.BUTTON_SUBMIT]: 'button[type="submit"]',
            [FORM.INPUT]: 'input',
            [FORM.INPUT_TEXT]: 'input[type="text"]',
            [FORM.INPUT_DATE]: 'input[type="date"]',
            [FORM.INPUT_FILE]: 'input[type="file"]',
            [FORM.INPUT_CHECKBOX]: 'input[type="checkbox"]',
            [FORM.INPUT_NUMBER]: 'input[type="number"]',
            [FORM.INPUT_RADIO]: 'input[type="radio"]',
            [FORM.TEXTAREA]: 'textarea',
            [FORM.SELECT]: 'select',
            [FORM.CONFIRM_SCREEN]: '.confirmer',
            [FORM.CONFIRM_PROMPT]: '.confirmer h4',
            [FORM.CONFIRM_BUTTON]: '.confirmer button'
        };
        
        Object
            .keys(styleToSelector)
            .filter(styleID => this.props.styles[styleID])
            .forEach(styleID => {
                Array
                    .from(el.querySelectorAll(styleToSelector[styleID]))
                    .forEach(selectedEl => {
                        Object
                            .entries(this.getElementStyles(styleID))
                            .forEach(([styleName, value]) => {
                                if (selectedEl.style[styleName] !== value) {
                                    selectedEl.style[styleName] = value;  
                                }
                            })
                    })
            })
    }

    get formData() {
        if (this.formEl) {
            return Object.fromEntries(new FormData(this.formEl));
        }

        return {};
    }

    // override actions to add defaults for showing confirm screen
    // and removing/ungating annotation once confirmed 
    get actions() {
        const actions = super.actions,
            hasSubmit = actions.some(a => a.event === 'onSubmit'),
            doesConfirm = actions.some(a => a.action === ACTIONS.CONFIRM_ACTION),
            handlesConfirm = actions.some(a => a.event === 'onConfirm');

        // if a submit action is defined but no confirm action
        // inject confirm action
        if (hasSubmit && !doesConfirm && !this.options.dontConfirm) {
            actions.push({
                event: 'onSubmit',
                action: ACTIONS.CONFIRM_ACTION,
                args: {
                    value: 'Submit Successful'
                }
            });
        } else if (!hasSubmit) {
            // add dummy submit handler to prevent form from reloading page
            actions.push({
                event: 'onSubmit',
                action: ''
            })
        }

        // handle confirm event
        // clean-up by removing gate or removing annotation
        if (!handlesConfirm) {
            const confirmAction = {
                event: 'onConfirm',
                args: { value: this.props.id }
            };

            confirmAction.action = (
                this.props.gated ? 
                    ACTIONS.REMOVE_GATE :
                    ACTIONS.HIDE_ANNOTATION
            );

            actions.push(confirmAction);
        }

        return actions;
    }

    getElementStyles(id) {
        const styles = super.getElementStyles(id);

        if (!id || id.endsWith('.root')) {
            return {
                ...styles,
                overflow: 'scroll'
            }
        }

        return styles;
    }

    get confirmStyles() {
        const { FORM } = COMPOSITION_TYPES;
        
        return [
            FORM.CONFIRM_SCREEN,
            FORM.CONFIRM_PROMPT,
            FORM.CONFIRM_BUTTON
        ].reduce((styles, name) => ({
            ...styles,
            [name]: this.getElementStyles(name)
        }), {})
    }

    static get composition() {
        return getCompositionInfo('FORM');
    }

    static get schemaType() {
        return 'form';
    }
}